/**
 * Classify the current CRM location into a page label plus optional job/lead entity.
 * Priority: project time context → lead time context → route params → page only.
 */
import type { CrmActivityClass, CrmActivityEntityType } from '../types';
import { inferCrmActivityClass } from './crmActivityClass';

export interface CrmActivityContext {
  path: string;
  pageKey: string;
  pageLabel: string;
  activityClass: CrmActivityClass;
  /** Project Hub job id when the entity is a project. */
  jobId?: string;
  dealId?: string;
  entityType?: CrmActivityEntityType;
  entityId?: string;
  entityName?: string;
  /** Query string (without leading ?) for module/tab attribution. */
  search?: string;
  /** In-page module/tab key (finances sub-page or Project Hub ?tab=). */
  moduleKey?: string;
  moduleLabel?: string;
  /** Spec alias for moduleKey (Finances/Materials/Project Hub tabs). */
  viewKey?: string;
  viewLabel?: string;
}

export interface ClassifyCrmActivityLocationInput {
  pathname: string;
  search?: string;
  activeProjectId?: string | null;
  activeLeadId?: string | null;
  activeDealId?: string | null;
  activeProposalId?: string | null;
  projectName?: string | null;
  leadName?: string | null;
  dealName?: string | null;
}

interface PageRule {
  match: RegExp;
  pageKey: string;
  pageLabel: string;
}

const SETTINGS_SECTION_LABELS: Record<string, string> = {
  account: 'My Account',
  integrations: 'Integrations',
  proposals: 'Proposal Rates',
  'client-experience': 'Client Experience',
  'project-defaults': 'Project Defaults',
  team: 'Team',
  crm: 'CRM',
  tools: 'Tools',
  api: 'API & Agents',
  'release-notes': 'Release Notes',
  achievements: 'Certifications & Achievements',
  feedback: 'Feedback',
  admin: 'Admin',
};

const FINANCES_SUBPAGE: Record<string, { pageKey: string; pageLabel: string }> = {
  '': { pageKey: 'finances-overview', pageLabel: 'Finances → Overview' },
  overview: { pageKey: 'finances-overview', pageLabel: 'Finances → Overview' },
  forecast: { pageKey: 'finances-overview', pageLabel: 'Finances → Overview' },
  'project-pl': { pageKey: 'finances-project-pl', pageLabel: 'Finances → Project P&L' },
  payroll: { pageKey: 'finances-payroll', pageLabel: 'Finances → Payroll' },
  'progress-billing': { pageKey: 'finances-progress-billing', pageLabel: 'Finances → Progress billing' },
  overhead: { pageKey: 'finances-overhead', pageLabel: 'Finances → Overhead' },
  'contractor-payables': { pageKey: 'finances-contractor-payables', pageLabel: 'Finances → Contractor payables' },
  regulatory: { pageKey: 'finances-regulatory', pageLabel: 'Finances → Regulatory' },
  capex: { pageKey: 'finances-capex', pageLabel: 'Finances → CapEx' },
  approvals: { pageKey: 'finances-approvals', pageLabel: 'Finances → Approvals' },
};

const PROJECT_HUB_TAB_LABELS: Record<string, string> = {
  table: 'Tasks',
  gantt: 'Gantt',
  files: 'Files',
  suppliers: 'Suppliers',
  changeOrders: 'Change orders',
  crew: 'Crew',
};

const PAGE_RULES: PageRule[] = [
  { match: /^\/project-hub(?:\/|$)/, pageKey: 'project-hub', pageLabel: 'Project Hub' },
  { match: /^\/shop-schedule/, pageKey: 'shop-schedule', pageLabel: 'Shop schedule' },
  { match: /^\/leads\/new(?:\/|$)/, pageKey: 'lead-new', pageLabel: 'New lead' },
  { match: /^\/leads\/[^/]+\/edit(?:\/|$)/, pageKey: 'lead-edit', pageLabel: 'Edit lead' },
  { match: /^\/leads(?:\/|$)/, pageKey: 'leads', pageLabel: 'Leads' },
  { match: /^\/list(?:\/|$)/, pageKey: 'leads', pageLabel: 'Leads' },
  { match: /^\/crm\/leads\//, pageKey: 'leads', pageLabel: 'Leads' },
  { match: /^\/lead\//, pageKey: 'leads', pageLabel: 'Leads' },
  { match: /^\/clients(?:\/|$)/, pageKey: 'clients', pageLabel: 'Clients' },
  { match: /^\/pipeline(?:\/|$)/, pageKey: 'pipeline', pageLabel: 'Pipeline' },
  { match: /^\/proposal-builder\//, pageKey: 'proposal-builder', pageLabel: 'Proposal builder' },
  { match: /^\/proposals/, pageKey: 'proposals', pageLabel: 'Proposals' },
  { match: /^\/won-deals/, pageKey: 'won-deals', pageLabel: 'Won deals' },
  { match: /^\/sales-activity/, pageKey: 'sales-activity', pageLabel: 'Sales activity' },
  { match: /^\/sales-flow/, pageKey: 'sales-flow', pageLabel: 'Sales flow' },
  { match: /^\/design-tracker/, pageKey: 'design-tracker', pageLabel: 'Design tracker' },
  { match: /^\/client\/[^/]+\/project\/[^/]+\/board/, pageKey: 'inspiration-board', pageLabel: 'Inspiration board' },
  { match: /^\/client\//, pageKey: 'client-portal-mgmt', pageLabel: 'Client portal' },
  { match: /^\/portal\//, pageKey: 'client-portal', pageLabel: 'Client portal' },
  // Finances handled specially below (sub-pages).
  { match: /^\/card-expenses/, pageKey: 'card-expenses', pageLabel: 'Card expenses' },
  { match: /^\/billing-payables/, pageKey: 'billing-payables', pageLabel: 'Billing & payables' },
  { match: /^\/purchasing/, pageKey: 'purchasing', pageLabel: 'Purchasing' },
  { match: /^\/contractor-timesheets/, pageKey: 'contractor-timesheets', pageLabel: 'Contractor timesheets' },
  { match: /^\/people-hub(?:\/|$)/, pageKey: 'people', pageLabel: 'People' },
  { match: /^\/people\/travel/, pageKey: 'people-travel', pageLabel: 'People → Travel' },
  { match: /^\/people\/handbook/, pageKey: 'people-handbook', pageLabel: 'People → Handbook' },
  { match: /^\/people\/benefits/, pageKey: 'people-benefits', pageLabel: 'People → Benefits' },
  { match: /^\/people\/hiring/, pageKey: 'people-hiring', pageLabel: 'People → Hiring' },
  { match: /^\/people\/team/, pageKey: 'people-team', pageLabel: 'People → Team' },
  { match: /^\/people\/achievements/, pageKey: 'people-achievements', pageLabel: 'People → Achievements' },
  { match: /^\/people\/org/, pageKey: 'people-org', pageLabel: 'People → Org chart' },
  { match: /^\/people(?:\/|$)/, pageKey: 'people', pageLabel: 'People' },
  { match: /^\/calendar(?:\/|$)/, pageKey: 'calendar', pageLabel: 'Calendar' },
  { match: /^\/executive-calendar/, pageKey: 'executive-calendar', pageLabel: 'Executive calendar' },
  { match: /^\/executive(?:\/|$)/, pageKey: 'executive', pageLabel: 'Executive' },
  { match: /^\/inbox(?:\/|$)/, pageKey: 'executive', pageLabel: 'Executive' },
  { match: /^\/briefings(?:\/|$)/, pageKey: 'briefings', pageLabel: 'Trade briefings' },
  { match: /^\/briefing\//, pageKey: 'briefing', pageLabel: 'Trade briefing' },
  { match: /^\/messages(?:\/|$)/, pageKey: 'messages', pageLabel: 'Messages' },
  { match: /^\/notifications(?:\/|$)/, pageKey: 'notifications', pageLabel: 'Notifications' },
  { match: /^\/marketing-hub/, pageKey: 'marketing-hub', pageLabel: 'Marketing hub' },
  { match: /^\/marketing\/meta/, pageKey: 'analytics-meta', pageLabel: 'Meta insights' },
  { match: /^\/analytics\/meta/, pageKey: 'analytics-meta', pageLabel: 'Meta insights' },
  { match: /^\/analytics/, pageKey: 'analytics', pageLabel: 'Analytics' },
  { match: /^\/reports(?:\/|$)/, pageKey: 'reports', pageLabel: 'Reports' },
  { match: /^\/utm-strategy/, pageKey: 'utm-strategy', pageLabel: 'UTM strategy' },
  { match: /^\/operations(?:\/|$)/, pageKey: 'operations', pageLabel: 'Operations' },
  { match: /^\/materials(?:\/|$)/, pageKey: 'materials', pageLabel: 'Materials' },
  { match: /^\/kit-/, pageKey: 'kits', pageLabel: 'Kits' },
  { match: /^\/shop-management/, pageKey: 'shop-management', pageLabel: 'Shop management' },
  { match: /^\/my-rfqs(?:\/|$)/, pageKey: 'my-rfqs', pageLabel: 'My RFQs' },
  { match: /^\/vendor-/, pageKey: 'vendors', pageLabel: 'Vendors' },
  { match: /^\/vendors(?:\/|$)/, pageKey: 'vendors', pageLabel: 'Vendors' },
  { match: /^\/supplier-rfqs/, pageKey: 'vendors', pageLabel: 'Vendors' },
  { match: /^\/(designer-portal|architect-portal|engineer-portal)/, pageKey: 'designer-portal', pageLabel: 'Design Portal' },
  { match: /^\/team(?:\/|$)/, pageKey: 'team-invites', pageLabel: 'Team invites' },
  { match: /^\/projects(?:\/|$)/, pageKey: 'projects', pageLabel: 'Projects' },
  { match: /^\/tools/, pageKey: 'tools', pageLabel: 'Tools' },
  { match: /^\/media-management/, pageKey: 'media', pageLabel: 'Media' },
  { match: /^\/map(?:\/|$)/, pageKey: 'map', pageLabel: 'CRM map' },
  { match: /^\/agent-activity/, pageKey: 'agent-activity', pageLabel: 'Agent activity' },
  { match: /^\/agent-harness/, pageKey: 'agent-harness', pageLabel: 'Agent harness' },
  { match: /^\/journey-management/, pageKey: 'journey', pageLabel: 'Journey' },
  { match: /^\/journey(?:\/|$)/, pageKey: 'journey-public', pageLabel: 'Journey' },
  { match: /^\/shed-calc(?:\/|$)/, pageKey: 'shed-calc', pageLabel: 'Shed calculator' },
  { match: /^\/import/, pageKey: 'import', pageLabel: 'Import' },
  { match: /^\/dxf-nesting/, pageKey: 'dxf-nesting', pageLabel: 'DXF nesting' },
  { match: /^\/invoice-review/, pageKey: 'invoice-review', pageLabel: 'Invoice review' },
  { match: /^\/project-pl\/cards/, pageKey: 'project-pl-cards', pageLabel: 'Debit cards' },
  { match: /^\/project-pl(?:\/|$)/, pageKey: 'finances-project-pl', pageLabel: 'Finances → Project P&L' },
  { match: /^\/external-portals/, pageKey: 'external-portals', pageLabel: 'External portals' },
  { match: /^\/portal-trail-preview/, pageKey: 'portal-trail-preview', pageLabel: 'Portal trail preview' },
  { match: /^\/project\/[^/]+/, pageKey: 'project-legacy', pageLabel: 'Project' },
  { match: /^\/gmail-callback/, pageKey: 'gmail-callback', pageLabel: 'Gmail callback' },
  { match: /^\/debug\/notifications/, pageKey: 'debug-notifications', pageLabel: 'Notification debug' },
  { match: /^\/login(?:\/|$)/, pageKey: 'login', pageLabel: 'Login' },
  { match: /^\/dashboard(?:\/|$)/, pageKey: 'dashboard', pageLabel: 'Dashboard' },
  { match: /^\/$/, pageKey: 'home', pageLabel: 'Home' },
];

/**
 * Representative authenticated CRM paths that must never classify as pageKey "other".
 * Used by the route-coverage CI test. Keep in sync with App.tsx routes.
 */
export const CRM_ROUTE_COVERAGE_PATHS: readonly string[] = [
  '/',
  '/dashboard',
  '/login',
  '/list',
  '/leads',
  '/leads/new',
  '/leads/abc/edit',
  '/clients',
  '/pipeline',
  '/proposal-builder/deal1',
  '/proposals',
  '/won-deals',
  '/sales-activity',
  '/sales-flow',
  '/design-tracker',
  '/calendar',
  '/executive',
  '/executive-calendar',
  '/inbox',
  '/messages',
  '/notifications',
  '/briefings',
  '/briefing/token1',
  '/finances',
  '/finances/overview',
  '/finances/project-pl',
  '/finances/payroll',
  '/finances/progress-billing',
  '/finances/overhead',
  '/finances/contractor-payables',
  '/finances/regulatory',
  '/finances/capex',
  '/finances/approvals',
  '/project-pl',
  '/project-pl/cards',
  '/card-expenses',
  '/billing-payables',
  '/purchasing',
  '/contractor-timesheets',
  '/invoice-review',
  '/people',
  '/people-hub',
  '/people/hiring',
  '/people/team',
  '/people/travel',
  '/people/handbook',
  '/people/benefits',
  '/people/achievements',
  '/people/org',
  '/project-hub',
  '/project-hub/proj1',
  '/project-hub/proj1/timeline-display',
  '/shop-schedule',
  '/materials',
  '/operations',
  '/marketing-hub',
  '/marketing/meta',
  '/analytics',
  '/analytics/meta',
  '/reports',
  '/utm-strategy',
  '/external-portals',
  '/my-rfqs',
  '/vendors',
  '/vendor-admin',
  '/vendor-portal',
  '/supplier-rfqs',
  '/designer-portal',
  '/architect-portal',
  '/engineer-portal',
  '/team',
  '/projects',
  '/tools',
  '/media-management',
  '/map',
  '/agent-activity',
  '/agent-harness',
  '/journey-management',
  '/journey',
  '/shed-calc',
  '/import',
  '/dxf-nesting',
  '/settings',
  '/settings/account',
  '/settings/team',
  '/settings/team/access',
  '/portal/proj1',
  '/client/lead1',
  '/client/lead1/project/deal1/board',
  '/project/legacy1',
  '/gmail-callback',
  '/portal-trail-preview/p1/d1',
  '/debug/notifications',
  '/crm/leads/lead1',
  '/lead/lead1',
];

function normalizePath(pathname: string): string {
  if (!pathname) return '/';
  const trimmed = pathname.trim() || '/';
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed;
}

function normalizeSearch(search?: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
}

function financesPage(path: string): { pageKey: string; pageLabel: string } | null {
  const m = path.match(/^\/finances(?:\/([^/]+))?/);
  if (!m) return null;
  const segment = (m[1] || '').toLowerCase();
  return FINANCES_SUBPAGE[segment] || {
    pageKey: `finances-${segment || 'overview'}`,
    pageLabel: `Finances → ${segment || 'Overview'}`,
  };
}

function pageFromPath(path: string): { pageKey: string; pageLabel: string } {
  if (path === '/settings/team/access' || path.startsWith('/settings/team/access/')) {
    return { pageKey: 'settings-team-access', pageLabel: 'Settings → Team → Role access' };
  }
  if (path === '/settings/team' || path.startsWith('/settings/team/')) {
    return { pageKey: 'people-team', pageLabel: 'People → Team' };
  }
  const settings = path.match(/^\/settings(?:\/([^/]+))?/);
  if (settings) {
    const section = settings[1] || 'account';
    const sectionLabel = SETTINGS_SECTION_LABELS[section] || section;
    return { pageKey: `settings-${section}`, pageLabel: `Settings → ${sectionLabel}` };
  }

  const finances = financesPage(path);
  if (finances) return finances;

  for (const rule of PAGE_RULES) {
    if (rule.match.test(path)) {
      return { pageKey: rule.pageKey, pageLabel: rule.pageLabel };
    }
  }

  // Unknown paths stay "other" so the route-coverage CI test can catch them.
  return { pageKey: 'other', pageLabel: 'CRM' };
}

function projectHubModule(
  path: string,
  search: string,
): { moduleKey?: string; moduleLabel?: string } {
  if (!/^\/project-hub(?:\/|$)/.test(path)) return {};
  const params = new URLSearchParams(search);
  const tab = (params.get('tab') || '').trim();
  if (!tab) return { moduleKey: 'table', moduleLabel: 'Tasks' };
  const label = PROJECT_HUB_TAB_LABELS[tab] || tab;
  return { moduleKey: tab, moduleLabel: label };
}

function materialsModule(
  path: string,
  search: string,
): { moduleKey?: string; moduleLabel?: string } {
  if (!/^\/materials(?:\/|$)/.test(path)) return {};
  const params = new URLSearchParams(search);
  const tab = (params.get('tab') || '').trim();
  if (tab === 'price-reviews') {
    return { moduleKey: 'materials-price-reviews', moduleLabel: 'Materials → Price reviews' };
  }
  if (tab === 'glulam') return { moduleKey: 'materials-glulam', moduleLabel: 'Materials → Glulam' };
  if (tab === 'hardware') return { moduleKey: 'materials-hardware', moduleLabel: 'Materials → Hardware' };
  if (tab === 'timber' || !tab) {
    return { moduleKey: 'materials-timber', moduleLabel: 'Materials → Timber' };
  }
  return { moduleKey: `materials-${tab}`, moduleLabel: `Materials → ${tab}` };
}

function moduleFromPage(
  path: string,
  search: string,
  page: { pageKey: string; pageLabel: string },
): { moduleKey?: string; moduleLabel?: string; viewKey?: string; viewLabel?: string } {
  const hub = projectHubModule(path, search);
  if (hub.moduleKey) {
    return { ...hub, viewKey: hub.moduleKey, viewLabel: hub.moduleLabel };
  }
  const materials = materialsModule(path, search);
  if (materials.moduleKey) {
    return { ...materials, viewKey: materials.moduleKey, viewLabel: materials.moduleLabel };
  }
  if (page.pageKey.startsWith('finances-')) {
    return {
      moduleKey: page.pageKey,
      moduleLabel: page.pageLabel,
      viewKey: page.pageKey,
      viewLabel: page.pageLabel,
    };
  }
  return {
    moduleKey: page.pageKey,
    moduleLabel: page.pageLabel,
    viewKey: page.pageKey,
    viewLabel: page.pageLabel,
  };
}

/** True when classifyCrmActivityLocation would report pageKey "other". */
export function crmActivityPageKeyIsOther(pathname: string, search?: string): boolean {
  return classifyCrmActivityLocation({ pathname, search }).pageKey === 'other';
}

function firstMatch(path: string, pattern: RegExp): string | undefined {
  const m = path.match(pattern);
  const value = m?.[1];
  return value && value.length > 0 ? value : undefined;
}

function entityFromRoute(path: string): {
  entityType?: CrmActivityEntityType;
  entityId?: string;
} {
  const projectId =
    firstMatch(path, /^\/project-hub\/([^/]+)/)
    || firstMatch(path, /^\/portal\/([^/]+)/)
    || firstMatch(path, /^\/project\/([^/]+)/);
  if (projectId && projectId !== 'timeline-display') {
    return { entityType: 'project', entityId: projectId };
  }

  const proposalDealId = firstMatch(path, /^\/proposal-builder\/([^/]+)/);
  if (proposalDealId) {
    return { entityType: 'deal', entityId: proposalDealId };
  }

  const leadEditId = firstMatch(path, /^\/leads\/([^/]+)\/edit/);
  if (leadEditId) {
    return { entityType: 'lead', entityId: leadEditId };
  }

  const clientLeadId = firstMatch(path, /^\/client\/([^/]+)/);
  if (clientLeadId) {
    return { entityType: 'lead', entityId: clientLeadId };
  }

  const crmLeadId =
    firstMatch(path, /^\/crm\/leads\/([^/]+)/)
    || firstMatch(path, /^\/lead\/([^/]+)/);
  if (crmLeadId) {
    return { entityType: 'lead', entityId: crmLeadId };
  }

  return {};
}

function withJobDealIds(ctx: Omit<CrmActivityContext, 'activityClass'>): Omit<CrmActivityContext, 'activityClass'> {
  if (ctx.entityType === 'project' && ctx.entityId) {
    return { ...ctx, jobId: ctx.entityId };
  }
  if ((ctx.entityType === 'deal' || ctx.entityType === 'proposal') && ctx.entityId) {
    return { ...ctx, dealId: ctx.entityId };
  }
  // Inspiration board embeds deal in the path; entity may still be lead.
  const boardDeal = firstMatch(ctx.path, /^\/client\/[^/]+\/project\/([^/]+)/);
  if (boardDeal) {
    return { ...ctx, dealId: ctx.dealId || boardDeal };
  }
  return ctx;
}

function withEntityName(
  ctx: Omit<CrmActivityContext, 'activityClass'>,
  input: ClassifyCrmActivityLocationInput,
): Omit<CrmActivityContext, 'activityClass'> {
  let next = ctx;
  if (ctx.entityType && ctx.entityId) {
    if (ctx.entityType === 'project' && input.projectName) {
      next = { ...ctx, entityName: input.projectName };
    } else if (ctx.entityType === 'lead' && input.leadName) {
      next = { ...ctx, entityName: input.leadName };
    } else if ((ctx.entityType === 'deal' || ctx.entityType === 'proposal') && (input.dealName || input.leadName)) {
      next = { ...ctx, entityName: input.dealName || input.leadName || undefined };
    }
  }
  return withJobDealIds(next);
}

function finalizeContext(ctx: Omit<CrmActivityContext, 'activityClass'>): CrmActivityContext {
  return {
    ...ctx,
    activityClass: inferCrmActivityClass({
      pageKey: ctx.pageKey,
      path: ctx.path,
      entityType: ctx.entityType,
    }),
  };
}

export function classifyCrmActivityLocation(input: ClassifyCrmActivityLocationInput): CrmActivityContext {
  const path = normalizePath(input.pathname || '/');
  const search = normalizeSearch(input.search);
  const page = pageFromPath(path);
  const module = moduleFromPage(path, search, page);
  const base = { path, search: search || undefined, ...page, ...module };

  const projectId = input.activeProjectId?.trim();
  if (projectId) {
    return finalizeContext(withEntityName({
      ...base,
      entityType: 'project',
      entityId: projectId,
    }, input));
  }

  const leadId = input.activeLeadId?.trim();
  if (leadId) {
    const proposalId = input.activeProposalId?.trim();
    const dealId = input.activeDealId?.trim();
    if (proposalId) {
      return finalizeContext(withEntityName({
        ...base,
        entityType: 'proposal',
        entityId: proposalId,
        entityName: input.leadName || undefined,
      }, input));
    }
    if (dealId) {
      return finalizeContext(withEntityName({
        ...base,
        entityType: 'deal',
        entityId: dealId,
      }, { ...input, dealName: input.dealName || input.leadName }));
    }
    return finalizeContext(withEntityName({
      ...base,
      entityType: 'lead',
      entityId: leadId,
    }, input));
  }

  return finalizeContext(withEntityName({ ...base, ...entityFromRoute(path) }, input));
}

export function crmActivitySessionKey(ctx: CrmActivityContext, skipSessionWrite: boolean): string {
  return [
    ctx.pageKey,
    ctx.viewKey || ctx.moduleKey || '',
    ctx.entityType || '',
    ctx.entityId || '',
    ctx.activityClass,
    skipSessionWrite ? 'skip' : 'write',
  ].join('|');
}

export function leadDisplayName(lead?: {
  firstName?: string;
  lastName?: string;
  company?: string;
} | null): string {
  if (!lead) return 'Unknown lead';
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (lead.company?.trim()) return lead.company.trim();
  return 'Unknown lead';
}
